import { useState, useEffect, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import {
  ArrowRight, Phone, Mail, Instagram, Loader2, CheckCircle2,
  Zap, Star
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useCanRender3D, HeroScene } from "./ThreeComponents";

gsap.registerPlugin(ScrollTrigger);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useScrollReveal = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

// Nav
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const h = () => setScrolled(window.scrollY > 40); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'nav-premium' : 'bg-transparent'}`} style={{ top: 0 }} data-testid="navigation">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-16 min-h-[64px]">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => window.scrollTo({top:0,behavior:'smooth'})} data-testid="logo">
            <img src="/mvs_pyramid_only.png" alt="MVS" className="h-8 sm:h-10 w-auto shrink-0" data-testid="nav-logo-img" />
            <span className="text-white font-medium text-xs sm:text-sm tracking-wide whitespace-nowrap">MODERN VISION SYSTEMS</span>
          </div>
          <div className="hidden md:flex items-center gap-7">
            {[{id:'services',l:'Services'},{id:'process',l:'Process'},{id:'about',l:'About'},{id:'contact',l:'Contact'}].map(n=>(
              <button key={n.id} onClick={()=>go(n.id)} className="text-[#888] hover:text-[#8A2BE2] transition-colors text-sm tracking-wide" data-testid={`nav-${n.id}`}>{n.l}</button>
            ))}
            <a href="https://calendly.com/mvs1207" target="_blank" rel="noopener noreferrer" className="bg-[#C9A84C] hover:bg-[#B09340] text-black font-semibold text-sm px-5 py-2 rounded transition-all" data-testid="nav-cta">Book a Consultation</a>
          </div>
          <a href="https://calendly.com/mvs1207" target="_blank" rel="noopener noreferrer" className="md:hidden bg-[#C9A84C] text-black font-semibold text-xs px-4 py-2 rounded" data-testid="mobile-cta">Book Call</a>
        </div>
      </div>
    </nav>
  );
};

// Hero — two-column desktop, stacked mobile
const Hero = ({ can3D }) => (
  <section className="hero-premium min-h-screen flex items-center pt-20 pb-12 relative" data-testid="hero-section">
    {can3D && <HeroScene />}
    <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10 w-full">
      {/* Mobile: small logo on top */}
      <div className="lg:hidden flex justify-center mb-6 animate-fade-in-up delay-1">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-[#C9A84C]/10 blur-[40px] scale-150"></div>
          <img src="/mvs_full_logo.png" alt="Modern Vision Systems" className="h-[140px] w-auto relative z-10" style={{ mixBlendMode: 'lighten' }} data-testid="hero-logo-mobile" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left: copy */}
        <div className="text-center lg:text-left min-w-0">
          <p className="animate-fade-in-up delay-1 text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.3em] font-medium mb-6 uppercase whitespace-nowrap">
            <span className="text-[#C9A84C]">Anchorage</span>
            <span className="text-[#8A2BE2] mx-2">&bull;</span>
            <span className="text-[#C9A84C]">Alaska</span>
            <span className="text-[#8A2BE2] mx-2">&bull;</span>
            <span className="text-[#C9A84C]">Veteran-Owned</span>
          </p>
          <h1 className="animate-fade-in-up delay-2 text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.2] mb-6" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="hero-title">
            Every business has a{" "}
            <span className="bg-gradient-to-r from-[#C9A84C] to-[#8A2BE2] bg-clip-text text-transparent">bottleneck.</span>
          </h1>
          <p className="animate-fade-in-up delay-3 text-lg text-[#ccc] mb-8 max-w-xl leading-relaxed lg:mx-0 mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            Modern Vision Systems is a consulting firm that finds yours — and builds the solution to fix it.
          </p>
          <div className="animate-fade-in-up delay-4 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
            <a href="https://calendly.com/mvs1207" target="_blank" rel="noopener noreferrer" className="cta-gold bg-[#C9A84C] hover:bg-[#B09340] text-black font-semibold tracking-wide text-sm px-8 py-3.5 rounded transition-all hover:shadow-[0_0_20px_rgba(138,43,226,0.3)]" data-testid="hero-cta-primary">
              BOOK A FREE CONSULTATION
            </a>
            <a href="#services" className="border border-[#8A2BE2]/50 text-[#8A2BE2] font-medium tracking-wide text-sm px-8 py-3.5 rounded transition-all hover:bg-[#8A2BE2]/10 hover:border-[#8A2BE2] hover:shadow-[0_0_15px_rgba(138,43,226,0.2)]" data-testid="hero-cta-secondary">
              HOW WE HELP
            </a>
          </div>
        </div>

        {/* Right: logo with glow (desktop only) */}
        <div className="hidden lg:flex items-center justify-center animate-fade-in-up delay-2">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#C9A84C]/8 blur-[60px] scale-125"></div>
            <img src="/mvs_full_logo.png" alt="Modern Vision Systems — Move Silent. Build Loud." className="max-h-[380px] w-auto relative z-10" style={{ mixBlendMode: 'lighten' }} data-testid="hero-logo" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services
const ServicesSection = () => {
  const [ref, vis] = useScrollReveal();
  const cards = [
    { title: "Solutions built for your specific problem", desc: "No templates. No one-size-fits-all. I diagnose the bottleneck in your operation and build the exact tool, system, or workflow that eliminates it." },
    { title: "Save time across your whole operation", desc: "Most small businesses lose hours every week to manual tasks, scattered tools, and broken processes. I consolidate and automate — so you get your time back." },
    { title: "Modern tools, including AI, where they fit", desc: "AI isn't the answer to everything. But where it fits — lead capture, customer support, data analysis, content — I build it into your operation so it actually works." },
  ];
  return (
    <section id="services" className="section-black py-24 md:py-32" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <h2 className="heading-accent text-3xl md:text-4xl font-semibold text-white mb-14 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>How We Help</h2>
        <div ref={ref} className={`grid md:grid-cols-3 gap-5 max-w-5xl mx-auto transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {cards.map((c,i) => (
            <div key={i} className="service-card bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-8 hover:border-[#8A2BE2]/30 hover:shadow-[0_0_25px_rgba(138,43,226,0.1)] transition-all duration-300" data-testid={`service-${i}`}>
              <h3 className="text-white text-lg font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{c.title}</h3>
              <p className="text-[#888] text-sm leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <a href="https://calendly.com/mvs1207" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#B09340] text-black font-semibold text-sm px-7 py-3 rounded transition-all hover:shadow-[0_0_20px_rgba(138,43,226,0.3)]">
            <ArrowRight className="w-3.5 h-3.5"/> Book a free consultation
          </a>
        </div>
      </div>
    </section>
  );
};

// The Standard
const StandardSection = () => {
  const [ref, vis] = useScrollReveal();
  const pillars = [
    { label: "DIRECT", desc: "Marine-direct communication. No corporate jargon. No runaround." },
    { label: "DONE", desc: "I sell outcomes. You get a finished solution, not a proposal." },
    { label: "PREMIUM", desc: "Built like a firm ten times our size. Delivered like a unit of one." },
  ];
  return (
    <section className="section-subtle py-24 md:py-32" data-testid="standard-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <h2 className="heading-accent text-3xl md:text-4xl font-semibold text-white mb-14 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>The Standard</h2>
        <div ref={ref} className={`grid md:grid-cols-3 gap-6 max-w-4xl mx-auto transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {pillars.map((p,i) => (
            <div key={i} className="text-center" data-testid={`standard-${i}`}>
              <p className="text-[#C9A84C] text-xs tracking-[0.3em] font-semibold mb-4">{p.label}</p>
              <div className="w-10 h-[1px] mx-auto mb-4 bg-gradient-to-r from-[#C9A84C] to-[#8A2BE2]"></div>
              <p className="text-[#aaa] text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ====== 3D PYRAMID SECTION ======
const PYRAMID_LAYERS = [
  { label: "DISCOVER", title: "Find the bottleneck", desc: "I audit your operation — workflows, tools, time sinks — and identify the single biggest thing slowing you down.", color: "#C9A84C" },
  { label: "DESIGN", title: "Architect the fix", desc: "No guesswork. I design a targeted solution — the right tool, system, or automation — scoped to your business.", color: "#8A2BE2" },
  { label: "BUILD", title: "Ship the solution", desc: "I build it fast and hand it over working. Websites, dashboards, automations, AI tools — whatever the problem calls for.", color: "#C9A84C" },
  { label: "OPERATE", title: "Keep it running", desc: "Optional ongoing support. I monitor, maintain, and improve — so you never have to think about it again.", color: "#8A2BE2" },
];

const PyramidSection = () => {
  const sectionRef = useRef(null);
  const [activeLayer, setActiveLayer] = useState(-1);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.5,
        onUpdate: (self) => {
          const p = self.progress;
          const newActive = p < 0.05 ? -1 : Math.min(3, Math.floor(p * 4.5));
          setActiveLayer(prev => prev !== newActive ? newActive : prev);
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="process" ref={sectionRef} style={{ height: '300vh' }} className="relative bg-[#0A0A0A]" data-testid="pyramid-section">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden pt-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
          <h2 className="heading-accent text-3xl md:text-4xl font-semibold text-white mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>The Process</h2>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-12">
            {/* SVG Pyramid — always visible */}
            <div className="w-[260px] h-[300px] md:w-[340px] md:h-[380px] flex-shrink-0">
              <svg viewBox="0 0 300 340" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                {[
                  { y: 0,   w1: 115, w2: 185, nw1: 90, nw2: 210, h: 72 },
                  { y: 76,  w1: 90,  w2: 210, nw1: 60, nw2: 240, h: 72 },
                  { y: 152, w1: 60,  w2: 240, nw1: 30, nw2: 270, h: 72 },
                  { y: 228, w1: 30,  w2: 270, nw1: 5,  nw2: 295, h: 72 },
                ].map((s, i) => {
                  const isActive = i === activeLayer;
                  const isPast = i < activeLayer;
                  const layer = PYRAMID_LAYERS[i];
                  const strokeCol = isActive ? layer.color : isPast ? "#C9A84C88" : "#C9A84C33";
                  const fillCol = isActive
                    ? (layer.color === "#8A2BE2" ? "rgba(138,43,226,0.2)" : "rgba(201,168,76,0.2)")
                    : isPast ? "rgba(201,168,76,0.06)" : "rgba(201,168,76,0.03)";
                  const glowFilter = isActive ? `drop-shadow(0 0 18px ${layer.color}80) drop-shadow(0 0 6px ${layer.color}40)` : 'none';
                  return (
                    <g key={i} style={{ filter: glowFilter, transition: 'filter 0.5s ease' }}>
                      <polygon
                        points={`${s.w1},${s.y} ${s.w2},${s.y} ${s.nw2},${s.y+s.h} ${s.nw1},${s.y+s.h}`}
                        fill={fillCol} stroke={strokeCol} strokeWidth={isActive ? 2 : 1}
                        style={{ transition: 'all 0.5s ease' }}
                      />
                      <text x="150" y={s.y + 44} textAnchor="middle"
                        fill={isActive ? "#fff" : isPast ? "#888" : "#555"}
                        fontSize="11" fontWeight={isActive ? "700" : "400"} letterSpacing="3"
                        style={{ fontFamily: "'Inter', sans-serif", transition: 'fill 0.4s' }}
                      >{PYRAMID_LAYERS[i].label}</text>
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Content cards */}
            <div className="w-full max-w-[360px] min-h-[220px] relative">
              {PYRAMID_LAYERS.map((layer, i) => (
                <div
                  key={i}
                  className={`absolute inset-0 transition-all duration-500 ${i === activeLayer ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}`}
                  data-testid={`pyramid-card-${i}`}
                >
                  <div className="rounded-lg p-7 border backdrop-blur-sm" style={{
                    background: 'rgba(15,15,15,0.95)',
                    borderColor: i === activeLayer ? `${layer.color}` : `${layer.color}40`,
                    boxShadow: i === activeLayer ? `0 0 25px ${layer.color}30, 0 0 50px ${layer.color}10` : `0 0 40px ${layer.color}08`
                  }}>
                    <p className="text-xs tracking-[0.25em] font-semibold mb-3" style={{ color: layer.color }}>{layer.label}</p>
                    <h3 className="text-white text-xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{layer.title}</h3>
                    <p className="text-[#999] text-sm leading-relaxed">{layer.desc}</p>
                  </div>
                </div>
              ))}
              {activeLayer === -1 && (
                <div className="flex items-center justify-center h-[220px]">
                  <p className="text-[#444] text-sm italic text-center">Scroll to explore the process</p>
                </div>
              )}
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-3 mt-8">
            {PYRAMID_LAYERS.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full transition-all duration-300 ${i === activeLayer ? 'scale-125' : 'scale-100 opacity-40'}`} style={{ backgroundColor: i === activeLayer ? l.color : '#333' }} />
                <span className={`text-[10px] tracking-widest font-medium transition-all duration-300 hidden sm:inline ${i === activeLayer ? 'opacity-100' : 'opacity-30'}`} style={{ color: i === activeLayer ? l.color : '#555' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// About
const AboutSection = () => {
  const [ref, vis] = useScrollReveal();
  return (
    <section id="about" className="section-black py-24 md:py-32" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <h2 className="heading-accent text-3xl md:text-4xl font-semibold text-white mb-14 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Built by a Marine in Anchorage</h2>
        <div ref={ref} className={`max-w-2xl mx-auto transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="space-y-5">
            <p className="text-[#ccc] text-base leading-relaxed">
              I'm Marvin Villaflores. Marine veteran. Anchorage, Alaska. I started Modern Vision Systems because I kept watching small businesses lose time and money to problems that had clear solutions — they just didn't have someone to build them.
            </p>
            <p className="text-[#999] text-base leading-relaxed">
              MVS is a consulting firm. I find the bottleneck in your business — the thing costing you the most time, money, or missed opportunities — and I build the solution. Websites, dashboards, automation, AI tools. Whatever the problem calls for.
            </p>
            <p className="text-[#999] text-base leading-relaxed">
              No agency overhead. No layers of account managers. One operator who answers his own phone and ships real work.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Guarantee
const GuaranteeSection = () => {
  const [ref, vis] = useScrollReveal();
  const items = [
    { icon: Zap, label: "Fast", desc: "Most solutions ship in days, not months." },
    { icon: CheckCircle2, label: "Flat-rate", desc: "Quoted upfront. No hidden fees. No surprise invoices." },
    { icon: Star, label: "Built until it works", desc: "I don't hand off problems. I solve them." },
  ];
  return (
    <section className="section-subtle py-24 md:py-32" data-testid="guarantee-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <h2 className="heading-accent text-3xl md:text-4xl font-semibold text-white mb-14 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>The Guarantee</h2>
        <div ref={ref} className={`grid md:grid-cols-3 gap-8 max-w-4xl mx-auto transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {items.map((g,i) => (
            <div key={i} className="text-center" data-testid={`guarantee-${i}`}>
              <g.icon className="w-7 h-7 text-[#8A2BE2] mx-auto mb-4"/>
              <h3 className="text-white font-semibold text-base mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{g.label}</h3>
              <p className="text-[#888] text-sm leading-relaxed">{g.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact
const ContactSection = () => {
  const [ref, vis] = useScrollReveal();
  const [form, setForm] = useState({ name:'', business:'', phone:'', need:'', notes:'' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.business ? `${form.business}@inquiry` : "inquiry@getmvs.net",
        phone: form.phone,
        message: `Business: ${form.business || 'N/A'}\nWhat they need: ${form.need || 'N/A'}\nNotes: ${form.notes || 'N/A'}`
      });
      setSubmitted(true);
      toast.success("Message received. I'll be in touch.");
    } catch { toast.error("Something went wrong. Text me at (907) 300-9396."); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="contact" className="section-black py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-xl mx-auto px-5 md:px-8">
        <h2 className="heading-accent text-3xl md:text-4xl font-semibold text-white mb-12 text-center" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="contact-title">Let's Talk</h2>
        <div ref={ref} className={`transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="space-y-4 mb-10">
            <a href="sms:+19073009396?body=BUILD" className="flex items-center gap-4 group" data-testid="contact-text">
              <Phone className="w-5 h-5 text-[#C9A84C] group-hover:text-[#8A2BE2] transition-colors"/>
              <div><p className="text-white text-sm font-medium">Text <span className="text-[#888]">(preferred)</span></p><p className="text-[#C9A84C]">(907) 300-9396</p></div>
            </a>
            <a href="mailto:mvs1207@outlook.com" className="flex items-center gap-4 group" data-testid="contact-email">
              <Mail className="w-5 h-5 text-[#C9A84C] group-hover:text-[#8A2BE2] transition-colors"/>
              <div><p className="text-white text-sm font-medium">Email</p><p className="text-[#C9A84C]">mvs1207@outlook.com</p></div>
            </a>
            <a href="https://www.instagram.com/mvs_907/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-ig">
              <Instagram className="w-5 h-5 text-[#C9A84C] group-hover:text-[#8A2BE2] transition-colors"/>
              <div><p className="text-white text-sm font-medium">Instagram</p><p className="text-[#C9A84C]">@mvs_907</p></div>
            </a>
          </div>
          <div className="border-t border-[#1A1A1A] mb-10"></div>
          {submitted ? (
            <div className="text-center py-10" data-testid="form-success">
              <CheckCircle2 className="w-10 h-10 text-[#8A2BE2] mx-auto mb-4"/>
              <h3 className="text-white text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Message received.</h3>
              <p className="text-[#888] text-sm">I'll be in touch within a few hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <Input name="name" placeholder="Name" value={form.name} onChange={handleChange} required className="bg-[#111] border-[#222] text-white placeholder:text-[#555] focus:border-[#8A2BE2] h-12 rounded" data-testid="form-name"/>
              <Input name="business" placeholder="Business name" value={form.business} onChange={handleChange} className="bg-[#111] border-[#222] text-white placeholder:text-[#555] focus:border-[#8A2BE2] h-12 rounded" data-testid="form-business"/>
              <Input name="phone" type="tel" placeholder="Phone" value={form.phone} onChange={handleChange} required className="bg-[#111] border-[#222] text-white placeholder:text-[#555] focus:border-[#8A2BE2] h-12 rounded" data-testid="form-phone"/>
              <Input name="need" placeholder="What do you need?" value={form.need} onChange={handleChange} className="bg-[#111] border-[#222] text-white placeholder:text-[#555] focus:border-[#8A2BE2] h-12 rounded" data-testid="form-need"/>
              <Textarea name="notes" placeholder="Anything else" value={form.notes} onChange={handleChange} rows={3} className="bg-[#111] border-[#222] text-white placeholder:text-[#555] focus:border-[#8A2BE2] rounded resize-none" data-testid="form-notes"/>
              <Button type="submit" disabled={submitting} className="w-full cta-gold bg-[#C9A84C] hover:bg-[#B09340] text-black font-semibold tracking-wide rounded py-3.5 text-sm hover:shadow-[0_0_20px_rgba(138,43,226,0.3)]" data-testid="form-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : "START CONVERSATION"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Footer with CTA glow
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-[#1A1A1A] py-12" data-testid="footer">
    <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
      <p className="text-lg tracking-[0.2em] font-semibold mb-1 bg-gradient-to-r from-[#C9A84C] to-[#8A2BE2] bg-clip-text text-transparent" style={{ fontFamily: "'Playfair Display', serif" }}>MVS</p>
      <p className="text-[#8A2BE2]/40 text-xs italic mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Move silent. Build loud.</p>
      <p className="text-[#555] text-xs mb-3">&copy; 2026 Modern Vision Systems LLC &middot; Anchorage, Alaska &middot; Veteran-Owned</p>
      <div className="flex items-center justify-center gap-4 text-xs">
        <a href="https://www.instagram.com/mvs_907/" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:text-[#8A2BE2] transition-colors" data-testid="footer-ig">@mvs_907</a>
        <span className="text-[#222]">&middot;</span>
        <span className="text-[#555]">getmvs.net</span>
      </div>
    </div>
  </footer>
);

export default function MarketingSite() {
  const can3D = useCanRender3D();
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Toaster position="top-right" richColors/>
      <Nav/>
      <main>
        <Hero can3D={can3D}/>
        <ServicesSection/>
        <StandardSection/>
        <PyramidSection/>
        <AboutSection/>
        <GuaranteeSection/>
        <ContactSection/>
      </main>
      <Footer/>
    </div>
  );
}
